/**
 * Phase 2 — investor onboarding wizard.
 *
 * A guided, multi-step renderer over the *same* question config, answer model,
 * and display-rule engine as the Phase 1 form (`BuyerProfileForm`). It adds no
 * backend of its own: answers go through the existing upsert endpoint, and the
 * resume cursor / completion status ride on `PUT /buyer-profile/onboarding`.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Button, Loader, Message } from 'semantic-ui-react';
import toast from 'react-hot-toast';
import {
  getBuyerProfile,
  getBuyerProfileQuestions,
  saveBuyerProfileAnswers,
  updateBuyerProfileOnboarding,
} from '~/api/buyerProfile';
import {
  AnswerPayload,
  BuyerProfileAnswerEntry,
  QuestionConfig,
  QuestionDef,
} from '../types';
import {
  AnswersMap,
  TimestampMap,
  answerKey,
  answersByKeyForDisplay,
  dropUnknownQuestions,
  entriesToMap,
  entriesToTimestampMap,
  latestTimestampForQuestion,
  mapToEntries,
  migrateLegacyNullSegmentAnswers,
} from '../answersState';
import { isQuestionVisible, visibleOptionsForQuestion } from '../displayRules';
import QuestionCard from '../QuestionCard';
import { ONBOARDING_STEPS } from './steps';
import { usePermissions } from '~/requests/permissions/usePermissions';
import '../styles.scss';

const OnboardingWizard: React.FC = () => {
  const history = useHistory();
  const { permissions, status: permissionsStatus } = usePermissions();
  const [config, setConfig] = useState<QuestionConfig | null>(null);
  const [initialAnswers, setInitialAnswers] = useState<BuyerProfileAnswerEntry[]>([]);
  const [initialStepIndex, setInitialStepIndex] = useState(0);
  const [companyId, setCompanyId] = useState<string>('');
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');

  useEffect(() => {
    let cancelled = false;
    setStatus('loading');
    Promise.all([getBuyerProfileQuestions(), getBuyerProfile()])
      .then(([cfg, profile]) => {
        if (cancelled) return;
        setConfig(cfg);
        setInitialAnswers(profile.answers ?? []);
        setInitialStepIndex(profile.onboardingState?.stepIndex ?? 0);
        setCompanyId(profile.companyId);
        setStatus('ready');
      })
      .catch(() => {
        if (!cancelled) setStatus('error');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Buyer profiles are admin-only for now (investor self-access is rolled
  // back). Gate the dormant onboarding route to admins, like CompanyProfile.
  if (permissionsStatus === 'loading') {
    return null;
  }
  if (!permissions?.isAdmin) {
    return <Message negative>Only admin users can access the buyer profile onboarding.</Message>;
  }

  if (status === 'loading') {
    return <Loader active inline="centered" content="Loading onboarding…" />;
  }
  if (status === 'error' || !config) {
    return (
      <Message negative>Could not load the onboarding wizard. Please try again.</Message>
    );
  }

  return (
    <WizardBody
      config={config}
      initialAnswers={initialAnswers}
      initialStepIndex={initialStepIndex}
      onExit={() => history.push(`/company-profile/${companyId}`)}
    />
  );
};

interface BodyProps {
  config: QuestionConfig;
  initialAnswers: BuyerProfileAnswerEntry[];
  initialStepIndex: number;
  onExit: () => void;
}

const WizardBody: React.FC<BodyProps> = ({
  config,
  initialAnswers,
  initialStepIndex,
  onExit,
}) => {
  const [answers, setAnswers] = useState<AnswersMap>(() =>
    migrateLegacyNullSegmentAnswers(
      dropUnknownQuestions(entriesToMap(initialAnswers), config.questions),
      config.questions
    )
  );
  const [timestamps, setTimestamps] = useState<TimestampMap>(() =>
    entriesToTimestampMap(initialAnswers)
  );
  const [stepIndex, setStepIndex] = useState(() =>
    Math.min(Math.max(initialStepIndex, 0), ONBOARDING_STEPS.length - 1)
  );
  const [saving, setSaving] = useState(false);

  const questionsByKey = useMemo(() => {
    const map: Record<string, QuestionDef> = {};
    config.questions.forEach((q) => {
      map[q.key] = q;
    });
    return map;
  }, [config.questions]);

  // Maps a market-segment option value to its label, for per-segment headings.
  const segmentLabels = useMemo(() => {
    const q = config.questions.find((x) => x.key === 'market_segment');
    const map: Record<string, string> = {};
    (q?.options ?? []).forEach((o) => {
      map[o.value] = o.label;
    });
    return map;
  }, [config.questions]);

  // Maps a US state code to its name, for the utility-zones group sub-headings.
  const stateLabels = useMemo(() => {
    const q = config.questions.find((x) => x.key === 'geography');
    const map: Record<string, string> = {};
    (q?.options ?? []).forEach((o) => {
      map[o.value] = o.label;
    });
    return map;
  }, [config.questions]);

  const displayInputs = useMemo(
    () => answersByKeyForDisplay(answers, config.questions),
    [answers, config.questions]
  );

  const step = ONBOARDING_STEPS[stepIndex];
  const isLastStep = stepIndex === ONBOARDING_STEPS.length - 1;

  // Questions for this step, in config order, minus any hidden by display rules.
  const stepQuestions = useMemo(
    () =>
      step.questionKeys
        .map((key) => questionsByKey[key])
        .filter(
          (q): q is QuestionDef =>
            !!q &&
            isQuestionVisible(q, displayInputs) &&
            (!q.optionGroupFilter ||
              visibleOptionsForQuestion(q, displayInputs).length > 0)
        ),
    [step, questionsByKey, displayInputs]
  );

  // Nested children are folded into their parent's card (see QuestionCard),
  // so they should not appear as top-level cards in the wizard step list
  // even when their step references them by key.
  const topLevelStepQuestions = useMemo(
    () => stepQuestions.filter((q) => !q.nestUnder),
    [stepQuestions]
  );
  const nestedChildrenByParent = useMemo(() => {
    const map: Record<string, QuestionDef[]> = {};
    for (const q of stepQuestions) {
      if (!q.nestUnder) continue;
      (map[q.nestUnder.question] ??= []).push(q);
    }
    return map;
  }, [stepQuestions]);

  const updateAnswer = (
    questionKey: string,
    segment: string | null,
    answer: AnswerPayload
  ) => {
    setAnswers((prev) => ({ ...prev, [answerKey(questionKey, segment)]: answer }));
  };

  /**
   * Persist answers + the resume cursor, then move. `complete` flips the
   * profile to 'onboarded' and exits to the full edit view.
   */
  const goToStep = async (target: number, complete = false) => {
    setSaving(true);
    const toastId = toast.loading('Saving…');
    try {
      const saved = await saveBuyerProfileAnswers(mapToEntries(answers));
      setTimestamps(entriesToTimestampMap(saved.answers ?? []));
      await updateBuyerProfileOnboarding({
        onboardingState: { stepIndex: target },
        ...(complete ? { status: 'onboarded' as const } : {}),
      });
      toast.success(complete ? 'Onboarding complete' : 'Progress saved', { id: toastId });
      if (complete) {
        onExit();
      } else {
        setStepIndex(target);
        window.scrollTo({ top: 0 });
      }
    } catch {
      toast.error('Could not save — check your answers and try again.', { id: toastId });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="buyer-profile-form buyer-profile-wizard">
      <div className="card buyer-profile-heading-card">
        <h2>Set up your buyer profile</h2>
        <p className="buyer-profile-subheading">
          A few quick steps to tell us what deals you&apos;re looking for. Every
          question is optional — you can stop any time and resume later.
        </p>
      </div>

      {/* Progress indicator */}
      <div className="card buyer-profile-wizard-progress">
        <ol className="buyer-profile-wizard-steps">
          {ONBOARDING_STEPS.map((s, i) => (
            <li
              key={s.id}
              className={i === stepIndex ? 'current' : i < stepIndex ? 'done' : undefined}
            >
              <span className="buyer-profile-wizard-step-num">{i + 1}</span>
              {s.title}
            </li>
          ))}
        </ol>
      </div>

      <div className="card buyer-profile-wizard-step-heading">
        <h3>
          Step {stepIndex + 1} of {ONBOARDING_STEPS.length}: {step.title}
        </h3>
        <p>{step.blurb}</p>
      </div>

      {topLevelStepQuestions.map((question) => (
        <QuestionCard
          key={question.key}
          question={question}
          answers={answers}
          segmentLabels={segmentLabels}
          answersByKey={displayInputs}
          groupLabels={stateLabels}
          updatedAt={latestTimestampForQuestion(timestamps, question.key)}
          onChange={updateAnswer}
          nestedChildren={nestedChildrenByParent[question.key]}
        />
      ))}

      <div className="buyer-profile-wizard-nav">
        <Button
          disabled={saving || stepIndex === 0}
          onClick={() => void goToStep(stepIndex - 1)}
        >
          Back
        </Button>
        <button
          type="button"
          className="buyer-profile-toc-link buyer-profile-wizard-exit"
          onClick={onExit}
        >
          Switch to full edit view
        </button>
        <Button
          primary
          loading={saving}
          disabled={saving}
          onClick={() => void goToStep(isLastStep ? stepIndex : stepIndex + 1, isLastStep)}
        >
          {isLastStep ? 'Finish' : 'Next'}
        </Button>
      </div>
    </div>
  );
};

export default OnboardingWizard;
